/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 *
 * Register rail: resumen numérico + CTA (display de caja).
 */
import React from 'react';
import { Text, TouchableOpacity, View } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { ticketColors } from '../../../shared/theme/ticketColors';
import { styles } from '../posShared.styles';
import { modalExperience } from '../../../shared/modals/modalExperience.styles';
import { MoneySummaryRow } from './MoneySummaryRow';
import { PosCheckoutTotalHero } from './PosCheckoutTotalHero';
import {
  CHECKOUT_MP_QR_SUMMARY_HINT,
  getCheckoutConfirmLabel,
  isMpQrOnlyDrafts,
} from './posCheckoutDerived';
import type { PosDraftPayment } from '../../domain/posTypes';
import type { CartEvaluationResult } from '../../../promotions/public-api';
import type { PosFiscalMode } from '../../domain/posTypes';
import { formatPosMoney } from '../../application/posMoneyFormat';
import { hasCheckoutPaymentProgress } from '../../application/posCheckoutPresentation';
import { usePosCheckoutContextOptional } from '../contexts/PosCheckoutContext';
import { useWebPosWriteGate } from '../../application/useWebPosWriteGate';

type Props = {
  variant: 'stickyFooter' | 'railEmbedded';
  total?: number;
  totalIngresado?: number;
  saldoPendiente?: number;
  vuelto?: number;
  paymentDrafts?: PosDraftPayment[];
  cartEvaluation?: CartEvaluationResult | null;
  saleFiscalMode?: PosFiscalMode;
  hasBlockingStockIssue?: boolean;
  isSaving?: boolean;
  showHero?: boolean;
  checkoutVisible?: boolean;
  onCompleteSale?: () => void;
  onRequestStockCorrection?: () => void;
};

export function PosCheckoutSummaryRail(props: Props) {
  const ctx = usePosCheckoutContextOptional();
  const { chargeBlocked } = useWebPosWriteGate();
  const {
    variant,
    total = 0,
    totalIngresado = 0,
    saldoPendiente = 0,
    vuelto = 0,
    paymentDrafts = [],
    cartEvaluation = null,
    saleFiscalMode = 'non_fiscal',
    hasBlockingStockIssue = false,
    isSaving = false,
    showHero = false,
    checkoutVisible = true,
    onCompleteSale = () => {},
    onRequestStockCorrection = () => {},
    promoUiStatus,
  } = {
    ...(ctx ?? {}),
    ...props,
  };
  const pendingTone = saldoPendiente > 0;
  const vueltoTone = vuelto > 0;
  const mpQrOnlyPayment = isMpQrOnlyDrafts(paymentDrafts);
  const hasMpQrPayment = paymentDrafts.some((d) => d.methodId === 'mercadopago_qr');
  // Solo mostrar el resumen cuando hay algo que informar: monto distinto del
  // total, pendiente/vuelto, o pago mixto. Tres filas de $0 son ruido.
  const hasPaymentProgress = hasCheckoutPaymentProgress({
    draftCount: paymentDrafts.length,
    saldoPendiente,
    vuelto,
    totalIngresado,
    total,
  });
  const confirmLabel = chargeBlocked
    ? 'Sin conexión'
    : promoUiStatus?.hasStaleSelection
      ? 'Promo vencida'
      : getCheckoutConfirmLabel(hasBlockingStockIssue, paymentDrafts);

  const confirmDisabled =
    isSaving || chargeBlocked || promoUiStatus?.hasStaleSelection === true;

  const containerStyle =
    variant === 'stickyFooter'
      ? [styles.checkoutStickyFooter, styles.checkoutSummaryRailSticky]
      : styles.checkoutSummaryRailEmbedded;

  return (
    <View style={containerStyle}>
      {showHero ? (
        <View style={styles.checkoutHeroBlockCompact}>
          <PosCheckoutTotalHero
            total={total}
            cartEvaluation={cartEvaluation}
            saleFiscalMode={saleFiscalMode}
            showFiscalHint={false}
            badgeText={paymentDrafts.length > 1 ? `${paymentDrafts.length} pagos` : undefined}
            checkoutVisible={checkoutVisible}
          />
        </View>
      ) : null}

      {mpQrOnlyPayment ? (
        <View style={styles.checkoutSummarySection}>
          <View style={styles.checkoutMpQrFooterPanel}>
            <View style={styles.checkoutMpQrFooterIconWrap}>
              <Ionicons name="qr-code-outline" size={20} color={ticketColors.brandInk} />
            </View>
            <View style={styles.checkoutMpQrFooterText}>
              <Text style={styles.checkoutMpQrFooterTitle}>QR de Mercado Pago</Text>
              <Text style={styles.checkoutMpQrFooterBody}>{CHECKOUT_MP_QR_SUMMARY_HINT}</Text>
            </View>
          </View>
        </View>
      ) : variant === 'stickyFooter' ? (
        hasPaymentProgress || hasMpQrPayment ? (
          <View style={styles.checkoutSummarySection}>
            {hasPaymentProgress ? (
              pendingTone ? (
                <View style={styles.checkoutDiffRow}>
                  <Text style={styles.checkoutDiffLabel}>Faltan</Text>
                  <Text style={[styles.checkoutDiffValue, styles.checkoutDiffValueWarn]}>
                    {formatPosMoney(saldoPendiente)}
                  </Text>
                </View>
              ) : (
                <View style={styles.checkoutDiffRow}>
                  <Text style={styles.checkoutDiffLabel}>Vuelto</Text>
                  <Text
                    style={[
                      styles.checkoutDiffValue,
                      vueltoTone ? styles.checkoutDiffValueOk : null,
                    ]}
                  >
                    {formatPosMoney(vuelto)}
                  </Text>
                </View>
              )
            ) : null}
            {hasMpQrPayment ? <Text style={styles.summaryHintFooter}>{CHECKOUT_MP_QR_SUMMARY_HINT}</Text> : null}
          </View>
        ) : null
      ) : hasPaymentProgress || hasMpQrPayment ? (
        <View style={styles.checkoutSummarySection}>
          <Text style={modalExperience.formSectionTitle}>Resumen del cobro</Text>
          <View style={styles.checkoutFooterTotals}>
            <MoneySummaryRow label="Ingresado" value={formatPosMoney(totalIngresado)} />
            <MoneySummaryRow
              label="Pendiente"
              value={formatPosMoney(saldoPendiente)}
              tone={pendingTone ? 'warn' : 'default'}
            />
            <MoneySummaryRow
              label="Vuelto"
              value={formatPosMoney(vuelto)}
              tone={vueltoTone ? 'success' : 'default'}
            />
          </View>
          {hasMpQrPayment ? <Text style={styles.summaryHintFooter}>{CHECKOUT_MP_QR_SUMMARY_HINT}</Text> : null}
        </View>
      ) : null}

      <View style={styles.checkoutActions}>
        <TouchableOpacity
          style={[
            styles.posPrimaryCta,
            styles.checkoutFooterPrimaryButton,
            confirmDisabled && { opacity: 0.6 },
          ]}
          onPress={hasBlockingStockIssue ? onRequestStockCorrection : onCompleteSale}
          disabled={confirmDisabled}
          testID="pos-checkout-confirm"
          accessibilityRole="button"
          accessibilityLabel={confirmLabel}
        >
          <Text style={[styles.posPrimaryCtaText, styles.checkoutFooterPrimaryButtonText]}>
            {isSaving ? 'Cobrando…' : confirmLabel}
          </Text>
        </TouchableOpacity>
      </View>
    </View>
  );
}
